import React, { useState, useEffect } from 'react';
import { TextField, Typography, Box, MenuItem, makeStyles, Theme } from '@material-ui/core';
import {
  subYears,
  getYear,
  getMonth,
  eachYearOfInterval,
  eachMonthOfInterval,
  format,
  set,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from 'date-fns';

import { GenerateReportRequest, Site, TimePeriod } from '../../../models';

import MainModal from 'src/components/ui/MainModal';
import DeliverySettings from './DeliverySettings';

interface Props {
  site: Site;
  reportId: number;
  reportName: string;
  availableTimePeriods: TimePeriod[];
  onSubmit: (values: GenerateReportRequest) => void;
  users: string[];
  email: string;
  closeForm: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  input: {
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: 20,
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
}));

const YEAR_LOOK_BACK_LIMIT = 2;

const getYears = (startDate?: string) => {
  const start = startDate ? new Date(startDate) : subYears(new Date(), YEAR_LOOK_BACK_LIMIT);
  const curr = new Date();
  return eachYearOfInterval({ start, end: curr }).map((year) => Number(format(year, 'yyyy')));
};

type Month = { index: number; label: string };
const getMonths = (year?: string, startDate?: string): Month[] => {
  const months: Month[] = [];
  const defaultYear = getYear(new Date());
  const monthList = eachMonthOfInterval({
    start: new Date(defaultYear, 0),
    end: new Date(defaultYear + 1, 0),
  });
  let startMonth = 0;
  let endMonth = 12;
  if (year && startDate) {
    const curr = new Date(new Date());
    const start = new Date(startDate);
    startMonth = Number(year) === getYear(start) ? getMonth(start) : startMonth;
    endMonth = Number(year) === getYear(curr) ? getMonth(curr) + 2 : endMonth;
  }
  for (let i = startMonth; i < endMonth; i++) {
    months.push({
      index: i,
      label: format(monthList[i], 'MMMM'),
    });
  }
  return months;
};

const calculateTimeRange = (timePeriod: TimePeriod, year?: number, month?: number) => {
  let startTS, endTS;

  if (month === undefined && TimePeriod.MONTHLY === timePeriod) {
    return { startTS: undefined, endTS: undefined };
  }

  if (!year && [TimePeriod.MONTHLY, TimePeriod.YEARLY].includes(timePeriod)) {
    return { startTS: undefined, endTS: undefined };
  }

  const yearSelectedDate = year ? set(new Date(), { year }) : undefined;
  const yearAndMonthSelectedDate =
    year && month !== undefined ? set(new Date(), { year, month }) : undefined;

  switch (timePeriod) {
    case TimePeriod.ALL:
      startTS = undefined;
      endTS = undefined;
      break;
    case TimePeriod.MONTHLY:
      startTS = yearAndMonthSelectedDate
        ? format(startOfMonth(yearAndMonthSelectedDate), 'yyyy-MM-dd')
        : undefined;
      endTS = yearAndMonthSelectedDate
        ? format(endOfMonth(yearAndMonthSelectedDate), 'yyyy-MM-dd')
        : undefined;
      break;
    case TimePeriod.YEARLY:
      startTS = yearSelectedDate ? format(startOfYear(yearSelectedDate), 'yyyy-MM-dd') : undefined;
      endTS = yearSelectedDate ? format(endOfYear(yearSelectedDate), 'yyyy-MM-dd') : undefined;
      break;
    case TimePeriod.MONTH_TO_DATE:
      startTS = format(startOfMonth(new Date()), 'yyyy-MM-dd');
      endTS = format(new Date(), 'yyyy-MM-dd');
      break;
    case TimePeriod.YEAR_TO_DATE:
      startTS = format(startOfYear(new Date()), 'yyyy-MM-dd');
      endTS = format(new Date(), 'yyyy-MM-dd');
      break;
    default:
      startTS = undefined;
      endTS = undefined;
  }

  return { startTS, endTS };
};

const ReportConfigurationForm: React.FC<Props> = ({
  availableTimePeriods,
  closeForm,
  email,
  onSubmit,
  reportId,
  reportName,
  site,
  users,
}) => {
  const classes = useStyles();

  const [timePeriod, setTimePeriod] = useState<TimePeriod | undefined>(undefined);
  const [yearSelected, setYearSelected] = useState<string | undefined>(undefined);
  const [monthSelected, setMonthSelected] = useState<string | undefined>(undefined);
  const [startTS, setStartTS] = useState<string | undefined>(undefined);
  const [endTS, setEndTS] = useState<string | undefined>(undefined);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [isTimePeriodTouched, setIsTimePeriodTouched] = useState(false);
  const [isYearSelectedTouched, setIsYearSelectedTouched] = useState(false);
  const [isMonthSelectedTouched, setIsMonthSelectedTouched] = useState(false);

  useEffect(() => {
    if (availableTimePeriods.length === 1) {
      setTimePeriod(availableTimePeriods[0]);
      setIsTimePeriodTouched(true);
    }
  }, [availableTimePeriods]);

  const handleAddRecipient = (newRecipients: string[]) => {
    setRecipients(newRecipients);
  };

  const handleRemoveRecipient = (emailAddress: string) => {
    setRecipients((prev) => prev.filter((email) => email !== emailAddress));
  };

  const handleChangeTimePeriod = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTimePeriod = event.target.value as TimePeriod;

    if (
      [TimePeriod.ALL, TimePeriod.YEAR_TO_DATE, TimePeriod.MONTH_TO_DATE].includes(newTimePeriod)
    ) {
      setYearSelected(undefined);
      setIsYearSelectedTouched(false);
      setMonthSelected(undefined);
      setIsMonthSelectedTouched(false);
    } else if (TimePeriod.YEARLY === newTimePeriod) {
      setMonthSelected(undefined);
      setIsMonthSelectedTouched(false);
    }

    const { startTS: newStartTS, endTS: newEndTS } = calculateTimeRange(
      newTimePeriod,
      yearSelected ? parseInt(yearSelected, 10) : undefined,
      monthSelected ? parseInt(monthSelected, 10) : undefined
    );

    setTimePeriod(newTimePeriod);
    setStartTS(newStartTS);
    setEndTS(newEndTS);
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newYear = event.target.value;

    if (timePeriod) {
      const { startTS: newStartTS, endTS: newEndTS } = calculateTimeRange(
        timePeriod,
        parseInt(newYear, 10),
        monthSelected !== undefined ? parseInt(monthSelected, 10) : undefined
      );
      setStartTS(newStartTS);
      setEndTS(newEndTS);
    }

    setYearSelected(newYear);
  };

  const handleMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMonth = event.target.value;

    if (timePeriod) {
      const { startTS: newStartTS, endTS: newEndTS } = calculateTimeRange(
        timePeriod,
        yearSelected ? parseInt(yearSelected, 10) : undefined,
        parseInt(newMonth, 10)
      );
      setStartTS(newStartTS);
      setEndTS(newEndTS);
    }

    setMonthSelected(newMonth);
  };

  const handleGenerateReport = () => {
    const generateReportRequest = {
      siteId: site.id,
      reportId,
      reportName,
      startTS,
      endTS,
      additionalRecipientEmails: recipients,
    };
    onSubmit(generateReportRequest);
  };

  const isYearSelectedRequired =
    timePeriod && [TimePeriod.YEARLY, TimePeriod.MONTHLY].includes(timePeriod);
  const isMonthSelectedRequired = timePeriod && [TimePeriod.MONTHLY].includes(timePeriod);

  const isValid =
    timePeriod &&
    ((isYearSelectedRequired && yearSelected) || (!isYearSelectedRequired && !yearSelected)) &&
    ((isMonthSelectedRequired && monthSelected !== undefined) ||
      (!isMonthSelectedRequired && monthSelected === undefined));

  const showTimePeriodError = isTimePeriodTouched && !Boolean(timePeriod);
  const showYearSelectedError =
    isYearSelectedTouched && !Boolean(yearSelected) && isYearSelectedRequired;
  const showMonthSelectedError =
    isMonthSelectedTouched && monthSelected === undefined && isMonthSelectedRequired;

  return (
    <MainModal
      disabledPrimaryButton={!isValid}
      isOpen
      onClose={closeForm}
      onPrimaryAction={handleGenerateReport}
      onSecondaryAction={closeForm}
      primaryButtonLabel="Generate"
      secondaryButtonLabel="Cancel"
      title="Configure Report"
      width={480}
    >
      <Box>
        <Typography className={classes.title}>{reportName}</Typography>
        <Box>
          <TextField
            className={classes.input}
            InputLabelProps={{
              shrink: Boolean(timePeriod),
            }}
            disabled={availableTimePeriods.length === 1}
            variant="outlined"
            size="small"
            fullWidth
            select
            label="Time Range"
            placeholder="Choose Time Range"
            onChange={handleChangeTimePeriod}
            onBlur={() => setIsTimePeriodTouched(true)}
            value={timePeriod || ''}
            error={showTimePeriodError}
            helperText={showTimePeriodError ? 'Time Range is required' : null}
          >
            {availableTimePeriods.map((period) => (
              <MenuItem key={period} value={period}>
                {period}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {isYearSelectedRequired && (
          <Box>
            <TextField
              className={classes.input}
              disabled={!timePeriod}
              InputLabelProps={{
                shrink: Boolean(yearSelected),
              }}
              variant="outlined"
              size="small"
              fullWidth
              select
              label="Year"
              onChange={handleYearChange}
              onBlur={() => setIsYearSelectedTouched(true)}
              value={yearSelected || ''}
              error={showYearSelectedError}
              helperText={showYearSelectedError ? 'Year is required' : null}
            >
              {getYears(site.commissionedOn).map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
        {isMonthSelectedRequired && (
          <Box>
            <TextField
              className={classes.input}
              disabled={!timePeriod}
              InputLabelProps={{
                shrink: monthSelected !== undefined,
              }}
              variant="outlined"
              size="small"
              fullWidth
              select
              label="Month"
              onChange={handleMonthChange}
              onBlur={() => setIsMonthSelectedTouched(true)}
              value={monthSelected === undefined ? '' : monthSelected}
              error={showMonthSelectedError}
              helperText={showMonthSelectedError ? 'Month is required' : null}
            >
              {getMonths(yearSelected, site.commissionedOn).map((month) => (
                <MenuItem key={month.label} value={month.index}>
                  {month.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
        <DeliverySettings
          email={email}
          handleAddRecipient={handleAddRecipient}
          handleRemoveRecipient={handleRemoveRecipient}
          recipients={recipients}
          users={users}
        />
      </Box>
    </MainModal>
  );
};

export default ReportConfigurationForm;
